/**
 * Save Menu
 * Disk icon button with dropdown for save/open project actions
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { HardDrive, Save, Upload, FileText } from 'lucide-react';
import { useEditorStore, useTemplateStore } from '@/stores';
import {
  createSlateFile,
  readSlateFile,
  downloadSlateFile,
  generateSlateFilename,
  captureCanvasPreview,
  designToTemplate,
  SLATE_EXTENSION,
} from '@/lib/slate';

interface SaveMenuProps {
  className?: string;
}

export function SaveMenu({ className = '' }: SaveMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { selectedFormat } = useEditorStore();
  const { currentTemplate, variableValues, loadTemplate, setVariableValues } = useTemplateStore();

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSave = useCallback(async () => {
    if (saving || !currentTemplate) return;
    setSaving(true);
    setIsOpen(false);

    try {
      const canvas = document.getElementById('ad-canvas');
      if (!canvas) throw new Error('Canvas not found');

      const previewBlob = await captureCanvasPreview(canvas, 1);

      const slateBlob = await createSlateFile(
        currentTemplate,
        selectedFormat,
        variableValues,
        previewBlob,
        {
          name: currentTemplate.metadata.name,
          description: currentTemplate.metadata.description,
        }
      );

      const filename = generateSlateFilename(
        currentTemplate.metadata.name,
        selectedFormat.id
      );
      downloadSlateFile(slateBlob, filename);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  }, [saving, currentTemplate, selectedFormat, variableValues]);

  const handleOpenClick = () => {
    fileInputRef.current?.click();
    setIsOpen(false);
  };

  const handleImport = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      const slateFile = await readSlateFile(file);
      const template = designToTemplate(slateFile.design, slateFile.manifest);
      loadTemplate(template);

      if (slateFile.design.variableValues) {
        setVariableValues(slateFile.design.variableValues);
      }
    } catch (error) {
      console.error('Open failed:', error);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [loadTemplate, setVariableValues]);

  const hasTemplate = currentTemplate !== null;

  return (
    <div ref={menuRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={saving || importing}
        className={`
          p-2 rounded-lg transition-colors flex items-center gap-1.5
          ${isOpen
            ? 'bg-white/10 text-white'
            : 'hover:bg-white/10 text-white/80 hover:text-white'
          }
          ${(saving || importing) ? 'opacity-50' : ''}
        `}
        title="Project"
        aria-label="Project"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <HardDrive size={16} />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1 w-64 bg-navy-light border border-white/10 rounded-lg shadow-xl overflow-hidden z-50"
          role="menu"
        >
          {/* Description */}
          <div className="px-3 py-2.5 border-b border-white/5">
            <div className="flex items-center gap-2 text-xs text-fg-muted mb-1">
              <FileText size={12} />
              <span>Project File</span>
            </div>
            <p className="text-xs text-fg-subtle leading-relaxed">
              Save as <code className="px-1 py-0.5 bg-white/10 rounded text-[11px]">{SLATE_EXTENSION}</code> to
              preserve layers, variables, and settings.
            </p>
          </div>

          <div className="p-1">
            <button
              onClick={handleSave}
              disabled={!hasTemplate || saving}
              className={`
                w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm transition-colors
                ${hasTemplate
                  ? 'hover:bg-white/10 text-fg'
                  : 'text-fg-subtle cursor-not-allowed'
                }
              `}
              role="menuitem"
            >
              <Save size={14} className="flex-none" />
              <div className="flex-1 min-w-0">
                <div className={hasTemplate ? 'text-fg' : 'text-fg-subtle'}>Save Project</div>
                <div className="text-xs text-fg-muted">Download {SLATE_EXTENSION} file</div>
              </div>
              <kbd className="text-[10px] text-fg-subtle bg-white/5 px-1.5 py-0.5 rounded">
                {navigator.platform.includes('Mac') ? '⌘S' : 'Ctrl+S'}
              </kbd>
            </button>

            <button
              onClick={handleOpenClick}
              disabled={importing}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm transition-colors hover:bg-white/10 text-fg"
              role="menuitem"
            >
              <Upload size={14} className="flex-none" />
              <div className="flex-1 min-w-0">
                <div className="text-fg">Open Project</div>
                <div className="text-xs text-fg-muted">Load {SLATE_EXTENSION} file</div>
              </div>
              <kbd className="text-[10px] text-fg-subtle bg-white/5 px-1.5 py-0.5 rounded">
                {navigator.platform.includes('Mac') ? '⌘O' : 'Ctrl+O'}
              </kbd>
            </button>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={SLATE_EXTENSION}
        onChange={handleImport}
        className="hidden"
      />
    </div>
  );
}
